'use client'

import { sendGTMEvent } from '@next/third-parties/google'
import Link from 'next/link'
import React, { useCallback, useState } from 'react'

import SmsOptInModal from 'lib/components/SmsOptInModal/SmsOptInModal'
import { emailPattern } from 'lib/utilities/string'

import useUserSubscriptionsApi from '@/api/useUserSubscriptionsApi'
import { useRecaptchaV3 } from '@/hooks/useRecaptchaV3'
import { useRudderstackAnalytics } from '@/hooks/useRudderstackAnalytics'
import { useSmsMarketingEnabled } from '@/hooks/useSmsMarketingEnabled'
import { brand } from '@/utils/brand'
import { queueFooterSmsOptInModal } from '@/utils/smsOptInModalQueue'

import { Button } from '../ui/Button'
import { CheckboxInput } from '../ui/CheckboxInput'
import { cn } from '../ui/cn'
import { Input } from '../ui/Input'

/**
 * Ship&Play "Travel Smarter" newsletter signup band. Ported from playground
 * `LeadCapture` — dark card, heading + subhead on the left, form (email
 * input + T&C checkbox + Submit button) on the right. Native react-router
 * `Link` swapped for `next/link`.
 *
 * Wired to the same subscription flow as the standard-brand `NewsletterSignup`
 * (see lib/component-library/components/NewsletterSignup): reCAPTCHA v3 token +
 * `useUserSubscriptionsApi` mutation + GTM `generate_lead` event. The Ship&Play
 * UI components don't forward refs / integrate with react-hook-form, so the
 * form is driven with controlled `useState`.
 */
export const LeadCapture = (): React.ReactElement => {
  const { ref } = useRudderstackAnalytics()
  const { executeRecaptcha } = useRecaptchaV3()
  const subscriptionMutation = useUserSubscriptionsApi()
  const { enabled: isSmsMarketingOn } = useSmsMarketingEnabled()

  const { brandName } = brand.layoutContent.siteChrome

  const [email, setEmail] = useState('')
  const [agreed, setAgreed] = useState(true)
  const [isSuccess, setIsSuccess] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const onSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      setErrorMessage(null)

      if (!emailPattern.test(email)) {
        setErrorMessage('Please enter a valid email address.')
        return
      }
      if (!agreed) {
        setErrorMessage('Please agree to the Terms of Use and Privacy Policy.')
        return
      }

      // Mint a reCAPTCHA v3 token before the mutation. The action name must
      // match the backend's verify_recaptcha(action: 'user_subscriptions')
      // call so Google grades the score against the right action threshold.
      const recaptchaToken = await executeRecaptcha('user_subscriptions')
      if (!recaptchaToken) {
        setErrorMessage('Verification failed. Please refresh and try again.')
        return
      }

      subscriptionMutation.mutate(
        {
          email,
          anonymous_id: ref?.current?.getAnonymousId(),
          'g-recaptcha-response': recaptchaToken,
        },
        {
          onSuccess: () => {
            setIsSuccess(true)

            sendGTMEvent({
              event: 'generate_lead',
              method: 'Ship&Play Homepage',
              user_email: email,
            })

            // Prompt for the SMS program once the email opt-in has landed,
            // seeding the modal with the email for backend identity stitching.
            // Queued with a short hold so the inline success state registers
            // before the modal takes over the screen.
            queueFooterSmsOptInModal(email)
          },
          onError: (error) => {
            setErrorMessage(error.message || 'Failed to subscribe. Please try again.')
          },
        },
      )
    },
    [agreed, email, executeRecaptcha, ref, subscriptionMutation],
  )

  return (
    <div className="snplay-bounds snplay:my-24">
      <section
        className={cn(
          'snplay:gap-8 snplay:rounded-2xl snplay:bg-ui-primary',
          'snplay:p-8 snplay:text-ui-foreground',
          'snplay:sm:flex snplay:sm:items-center',
        )}
      >
        <div className="snplay:flex-1">
          <h1 className="snplay:mb-4 snplay:text-4xl snplay:font-bold">
            Travel <span className="snplay:text-primary"> Smarter</span>
          </h1>
          <p className="snplay:mt-4 snplay:text-lg snplay:text-ui-foreground snplay:max-sm:mb-8">
            Subscribe for tips, deals, and more.
          </p>
        </div>
        {isSuccess ? (
          <p
            className={cn(
              'snplay:text-lg snplay:font-semibold',
              'snplay:sm:max-w-80 snplay:md:max-w-100 snplay:lg:max-w-140',
            )}
          >
            Thank you for signing up!
          </p>
        ) : (
          <form
            onSubmit={onSubmit}
            className="snplay:sm:max-w-80 snplay:md:max-w-100 snplay:lg:max-w-140"
          >
            <Input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter email address"
              className="snplay:w-full"
              inputClassName={
                'snplay:border-transparent snplay:hover:border-transparent' +
                ' snplay:focus-visible:border-transparent'
              }
            />
            <CheckboxInput
              label={
                <>
                  I agree to {brandName}{' '}
                  <Link href="/terms" className="snplay-link snplay-can-focus" target="_blank">
                    Terms of Use
                  </Link>{' '}
                  and{' '}
                  <Link href="/privacy" className="snplay-link snplay-can-focus" target="_blank">
                    Privacy Policy
                  </Link>
                  .
                </>
              }
              variant="inverted"
              name="i_agree"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="snplay:mt-2"
            />
            {errorMessage && (
              <p className="snplay:mt-4 snplay:text-sm snplay:text-red-400">{errorMessage}</p>
            )}
            <Button
              type="submit"
              variant="white"
              size="large"
              className="snplay:mt-6 snplay:bg-background"
              disabled={!email || !agreed || subscriptionMutation.isPending}
            >
              {subscriptionMutation.isPending ? 'Subscribing...' : 'Submit'}
            </Button>
          </form>
        )}
      </section>
      {/* Flag-gated so the modal ships zero DOM until the rollout is live. */}
      {isSmsMarketingOn && <SmsOptInModal brand="shipplay" source="FOOTER" />}
    </div>
  )
}
